import { Loader2, Truck, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import type { Operator, Trip } from "@/api.types.v2";
import { useOperators } from "@/api/operators";
import { useAssignOperatorToTrip } from "@/api/trips";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import { colorFromId } from "./trip-colors";
import { getOperatorDisplayName, getOperatorInitials } from "@/lib/safe-access";
function statusLabel(op: Operator): { label: string; color: string } {
  if (op.status === "ON_DUTY") return { label: "On duty", color: "#16a34a" };
  if (op.status === "BLOCKED") return { label: "Blocked", color: "#dc2626" };
  return { label: "Off duty", color: "#a1a1aa" };
}

interface AssignDriverDialogProps {
  trip: Trip | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Gesture ② — staff a whole trip to one operator (useAssignOperatorToTrip →
 * /operator). Kept separate from task→trip assignment.
 */
export function AssignDriverDialog({
  trip,
  open,
  onOpenChange,
}: AssignDriverDialogProps) {
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  const { data, isLoading } = useOperators({
    carrierIds: carrierIds.join(","),
    dispatchCenterIds: dispatchCenterIds.join(","),
    limit: 200,
  });
  const operators = (data?.data ?? []).filter((o) => o.status !== "BLOCKED");

  const assign = useAssignOperatorToTrip(trip?.id ?? "", {
    onSuccess: () => {
      toast.success("Driver assigned", { description: "Trip is now staffed." });
      onOpenChange(false);
    },
    onError: () => toast.error("Couldn't assign driver"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-[420px]">
        {trip && (
          <div className="dv2 flex flex-col">
            <div className="flex items-center gap-2 border-b border-zinc-200 px-4 py-3">
              <span
                className="rounded"
                style={{
                  width: 5,
                  height: 24,
                  background: trip.color ?? "#6366f1",
                }}
              />
              <UserPlus className="h-4 w-4 text-indigo-600" />
              <span className="text-[14px] font-bold text-zinc-900">
                Assign driver →{" "}
                <span className="font-mono">#{trip.tripNumber}</span>
              </span>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-2">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
                </div>
              ) : operators.length === 0 ? (
                <div className="px-3 py-6 text-center text-[12.5px] text-zinc-400">
                  No operators in scope
                </div>
              ) : (
                operators.map((op) => {
                  const sm = statusLabel(op);
                  return (
                    <button
                      key={op.id}
                      type="button"
                      disabled={assign.isPending}
                      onClick={() => assign.mutate(op.id)}
                      className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left hover:bg-zinc-50 disabled:opacity-50"
                    >
                      <div
                        className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[12px] font-bold text-white"
                        style={{ background: colorFromId(op.id) }}
                      >
                        {getOperatorInitials(op)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] font-semibold text-zinc-900">
                          {getOperatorDisplayName(op)}
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
                          {op.vehicle?.code && (
                            <span className="inline-flex items-center gap-1">
                              <Truck className="h-3 w-3" />
                              {op.vehicle.code}
                            </span>
                          )}
                          <span
                            style={{ color: sm.color }}
                            className="font-semibold"
                          >
                            {sm.label}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
